import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  Crown,
  Building2,
  Check,
  ArrowRight,
  Sparkles,
  Mail,
  CheckCircle2,
} from "lucide-react";
import PremiumModal from "./PremiumModal";
import { useUserDataContext } from "../../context/UserDataContext";
import { firmApi } from "../../services/api";

interface UpgradeGateModalProps {
  open: boolean;
  onClose: () => void;
  featureName: string;
  requiredPlan: "pro" | "elite";
}

const PLAN_INFO: Record<
  "pro" | "elite",
  {
    displayName: string;
    icon: React.FC<any>;
    color: string;
    bgColor: string;
    borderColor: string;
    monthlyPrice: number;
    annualPrice: number;
    features: string[];
  }
> = {
  pro: {
    displayName: "Professional",
    icon: Crown,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    monthlyPrice: 69,
    annualPrice: 55,
    features: [
      "Everything in Starter",
      "Messaging & Calls",
      "Leads & Intake Pipeline",
      "E-Signatures",
      "Custom Pipeline Stages",
      "Payment Plans & Autopay",
    ],
  },
  elite: {
    displayName: "Firm",
    icon: Building2,
    color: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    monthlyPrice: 99,
    annualPrice: 79,
    features: [
      "Everything in Professional",
      "Immigration Case Management",
      "Real Estate Module",
      "Trust Accounting",
      "Custom Branding",
      "Priority Support",
    ],
  },
};

const UpgradeGateModal: React.FC<UpgradeGateModalProps> = ({
  open,
  onClose,
  featureName,
  requiredPlan,
}) => {
  const navigate = useNavigate();
  const { userData, firmData } = useUserDataContext();
  const [requestState, setRequestState] = useState<"idle" | "sending" | "sent">("idle");

  const canManageBilling =
    (!!userData._id &&
      !!firmData?.adminUserId &&
      userData._id === firmData.adminUserId) ||
    userData.permissions?.canManageBilling === true;

  const plan = PLAN_INFO[requiredPlan];
  const PlanIcon = plan.icon;

  // Reset request state when modal closes
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => setRequestState("idle"), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Auto-close after successful request
  useEffect(() => {
    if (requestState === "sent") {
      const timer = setTimeout(() => onClose(), 3000);
      return () => clearTimeout(timer);
    }
  }, [requestState, onClose]);

  const handleRequestFromAdmin = async () => {
    setRequestState("sending");
    try {
      await firmApi.requestFeature({ featureName, requiredPlan });
      setRequestState("sent");
    } catch {
      setRequestState("idle");
    }
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title={`Unlock ${featureName}`}
      subtitle={
        canManageBilling
          ? `Upgrade to ${plan.displayName} to access this feature`
          : "This feature requires a plan upgrade"
      }
      icon={Lock}
      size="md"
    >
      {canManageBilling ? (
        /* -- Admin View: Plan card + Upgrade CTA -- */
        <div className="space-y-6">
          {/* Plan highlight card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className={`rounded-xl border-2 ${plan.borderColor} ${plan.bgColor} p-6 relative overflow-hidden`}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/20 to-transparent rounded-bl-full" />

            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`h-10 w-10 rounded-xl ${plan.bgColor} border ${plan.borderColor} flex items-center justify-center`}
                >
                  <PlanIcon className={`h-5 w-5 ${plan.color}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${plan.color}`}>
                    {plan.displayName}
                  </h3>
                  <p className="text-sm text-slate-500">
                    <span className="font-bold text-slate-800">
                      ${plan.annualPrice}
                    </span>
                    /user/mo{" "}
                    <span className="text-xs">(billed annually)</span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2">
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check
                      className={`h-4 w-4 flex-shrink-0 ${plan.color}`}
                    />
                    <span className="text-sm text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Feature callout */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-lg p-4"
          >
            <Sparkles className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-semibold">{featureName}</span> is included
              in the {plan.displayName} plan and above.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            type="button"
            onClick={() => {
              onClose();
              navigate("/settings/subscription");
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:scale-[1.01]"
            style={{ backgroundColor: "#0F5132" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#146C43";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#0F5132";
            }}
          >
            Upgrade Now
            <ArrowRight className="h-4 w-4" />
          </motion.button>

          {/* Dismiss link */}
          <div className="text-center">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      ) : (
        /* -- Non-Admin View: Request from admin -- */
        <div className="text-center py-4 space-y-6">
          <AnimatePresence mode="wait">
            {requestState === "sent" ? (
              /* Success state */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="space-y-4 py-4"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.1 }}
                  className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center"
                >
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <h3 className="text-lg font-bold text-slate-800 mb-1">
                    Request Sent!
                  </h3>
                  <p className="text-sm text-slate-500">
                    Your administrator has been notified.
                  </p>
                </motion.div>
              </motion.div>
            ) : (
              /* Default non-admin state */
              <motion.div
                key="default"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className="mx-auto h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center"
                >
                  <Lock className="h-8 w-8 text-slate-400" />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="space-y-2"
                >
                  <h3 className="text-lg font-bold text-slate-800">
                    {plan.displayName} Plan Required
                  </h3>
                  <p className="text-sm text-slate-500 max-w-xs mx-auto">
                    <span className="font-semibold text-slate-700">
                      {featureName}
                    </span>{" "}
                    requires the {plan.displayName} plan. Request an upgrade from
                    your firm administrator.
                  </p>
                </motion.div>

                {/* Request from Admin button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                  type="button"
                  disabled={requestState === "sending"}
                  onClick={handleRequestFromAdmin}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseEnter={(e) => {
                    if (requestState !== "sending") {
                      e.currentTarget.style.backgroundColor = "#146C43";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#0F5132";
                  }}
                >
                  {requestState === "sending" ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full"
                      />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Request from Admin
                    </>
                  )}
                </motion.button>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="text-xs text-slate-400"
                >
                  We'll notify your firm's administrator
                </motion.p>

                {/* Dismiss link */}
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Maybe Later
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </PremiumModal>
  );
};

export default UpgradeGateModal;
